/**
 * CVANTO CANONICAL FASHION ATTRIBUTE SYSTEM (§42) — the ONE source of truth for
 * every structured fashion/kids/accessories characteristic.
 *
 *   Create Listing  ->  stored listing.attributes  ->  search engine / manual filters
 *   ->  AI ShoppingIntent  ->  Shopping Profile  ->  personalised ranking
 *
 * All of them read THIS file (the server and the mobile app get generated mirrors:
 * `node server/scripts/generateAttributeSchema.js`). Nothing about fashion
 * attributes is listed anywhere else.
 *
 * Rules of the model
 *  - A FACET is one canonical characteristic (season, material, fitType, rise…).
 *  - A facet VALUE has a stable `id` (what intents, profiles and filters speak) and a
 *    `label` (the Ukrainian string a seller picks and a listing stores — existing
 *    convention, so old listings keep working). `stems` recognise the same value in
 *    LEGACY free text ("Еко шкіра", "бавовна 100%", "Синий") without any migration.
 *  - A facet reads from `keys`: the first is the key new listings are written under; the
 *    rest are legacy names the same concept lived under (seasonOuter, clothingSeason,
 *    shoesSeason, the dress-only `fit`…). Nothing is renamed in stored data.
 *  - Which facets apply to which kind of product is `CLASS_FACETS` (the conditional
 *    field matrix). Create Listing shows exactly those fields, filters offer exactly
 *    those facets, the Agent accepts exactly those attributes.
 *
 * Plain data + pure functions, ZERO imports (the generator mirrors this file mechanically).
 * Only `export const` is allowed (generator constraint).
 */

export const normalizeFacetText = (s) =>
  String(s === null || s === undefined ? "" : s)
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[’'`ʼ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/* ------------------------------ colour families ------------------------------ */

// Colour is free text on legacy listings ("Чорний ", "Беж", "Голубий", "Синий (класичний джинс)"):
// it can only be matched by FAMILY. A small closed set of stems a colour word must START with.
export const COLOR_FAMILY_STEMS = {
  білий: ["біл", "white"],
  чорний: ["чорн", "black"],
  сірий: ["сір", "grey", "gray", "графіт", "сталев"],
  синій: ["син", "navy", "blue", "індиго", "електрик"],
  блакитний: ["блакит", "голуб", "бірюз", "бирюз", "turquoise", "небесн"],
  червоний: ["червон", "red"],
  бордовий: ["бордо", "вишн", "марсала"],
  рожевий: ["рожев", "розов", "пудр", "pink", "малин", "фукс"],
  зелений: ["зелен", "green", "салат", "смарагд", "мят", "м'ят"],
  оливковий: ["олив", "хакі", "khaki"],
  жовтий: ["жовт", "лимон", "yellow", "гірчич", "мустард"],
  помаранчевий: ["помаранч", "оранж", "orange", "теракот", "персик", "корал"],
  фіолетовий: ["фіолет", "бузков", "ліловий", "purple", "лаванд"],
  коричневий: ["корич", "шоколад", "brown", "кава", "кемел", "camel", "мокко", "карамел"],
  бежевий: ["беж", "кремов", "кремовий", "пісочн", "beige", "молочн", "айворі", "ivory", "екрю"],
  золотий: ["золот", "gold"],
  срібний: ["срібл", "silver"],
  різнокольоровий: ["різнокол", "мультикол", "кольоров"],
};

export const COLOR_FAMILY_IDS = Object.keys(COLOR_FAMILY_STEMS);

// Words that link colours in a colour field ("чорний і білий", "яскраво-рожевий").
export const COLOR_GLUE_WORDS = ["і", "й", "та", "з", "із", "зі", "в", "у", "and", "колір", "кольору", "кольор", "світло", "темно", "яскраво", "ніжно", "блідо", "насичено"];

export const colorFamilyOfWord = (word) => {
  const w = normalizeFacetText(word);
  if (!w) return null;
  for (const family of COLOR_FAMILY_IDS) {
    for (const stem of COLOR_FAMILY_STEMS[family]) if (w.startsWith(stem)) return family;
  }
  return null;
};

// All colour families a free-text colour field names ("Чорний/ синій/ білий" -> [чорний, синій, білий]).
export const colorFamiliesOfText = (raw) => {
  const out = [];
  const text = Array.isArray(raw) ? raw.join(" ") : String(raw === null || raw === undefined ? "" : raw);
  for (const word of normalizeFacetText(text).split(/[^a-zа-яіїєґ'’-]+/).filter(Boolean)) {
    for (const part of word.split("-")) {
      if (COLOR_GLUE_WORDS.includes(part)) continue;
      const family = colorFamilyOfWord(part);
      if (family && !out.includes(family)) out.push(family);
    }
  }
  return out;
};

/* ---------------------------------- facets ----------------------------------- */

const v = (id, label, stems, extra) => ({ id, label, stems: stems || [], ...(extra || {}) });

export const FACETS = {
  // ---- when
  season: {
    label: "Сезон",
    kind: "select",
    keys: ["season", "seasonOuter", "clothingSeason", "shoesSeason"],
    // "Універсальне / Цілий рік" fits every season; "Демісезон" fits spring and autumn (see seasonCompatible)
    values: [
      v("summer", "Літо", ["літ", "summer"]),
      v("autumn", "Осінь", ["осін"]),
      v("winter", "Зима", ["зим", "winter"]),
      v("spring", "Весна", ["весн"]),
      v("demi", "Демісезон", ["демісез", "демисез"]),
      v("all_year", "Універсальне", ["універсал", "цілий рік", "цілорічн", "всесезон"]),
    ],
  },

  // ---- what it is made of
  material: {
    label: "Матеріал",
    kind: "multi",
    keys: ["material"],
    values: [
      v("cotton", "Бавовна", ["бавовн", "cotton", "котон"]),
      v("linen", "Льон", ["льон", "льнян", "лля", "linen"]),
      v("silk", "Шовк", ["шовк", "silk"]),
      v("wool", "Вовна", ["вовн", "wool", "шерст", "меринос"]),
      v("cashmere", "Кашемір", ["кашемір", "cashmere"]),
      v("denim", "Джинс", ["джинс", "denim"]),
      v("knit", "Трикотаж", ["трикотаж", "в'яза", "вязан"]),
      v("leather", "Натуральна шкіра", ["шкір", "leather"], { excludes: ["екошкір", "еко шкір", "еко-шкір", "штучн", "faux"] }),
      v("faux_leather", "Екошкіра", ["екошкір", "еко шкір", "еко-шкір", "штучна шкір", "штучної шкір", "leatherette"]),
      v("suede", "Замша", ["замш", "нубук", "suede"]),
      v("fur", "Хутро", ["хутр", "норк", "fur"], { excludes: ["еко", "штучн", "faux"] }),
      v("faux_fur", "Еко-хутро", ["еко хутр", "еко-хутр", "штучне хутр", "штучного хутр"]),
      v("velvet", "Велюр / оксамит", ["велюр", "оксамит", "velvet"]),
      v("satin", "Атлас / сатин", ["атлас", "сатин", "satin"]),
      v("viscose", "Віскоза", ["віскоз", "viscose"]),
      v("polyester", "Поліестер", ["поліестер", "полиэстер", "polyester"]),
      v("nylon", "Нейлон / плащівка", ["нейлон", "поліамід", "плащів", "nylon"]),
      v("down", "Пух", ["пух", "down"]),
      v("padding", "Синтепон / утеплювач", ["синтепон", "холлофайбер", "поліфайбер", "утеплювач"]),
      v("textile", "Текстиль", ["текстил", "тканин", "textile"]),
      v("rubber", "Гума / силікон", ["гум", "силікон", "eva"]),
      v("metal", "Метал", ["метал", "срібл", "золот", "сталь", "сталев", "бронз", "мельхіор", "бижутер"]),
    ],
  },

  // ---- how it fits / is cut
  fitType: {
    label: "Крій",
    kind: "select",
    keys: ["fitType"],
    values: [
      v("slim", "Облягаючий", ["облягаюч", "slim", "завужен", "скін", "skinny"]),
      v("regular", "Прямий", ["прям", "straight", "regular", "класичн"]),
      v("relaxed", "Вільний", ["вільн", "relaxed", "wide", "широк", "mom", "балон"]),
      v("oversized", "Оверсайз", ["оверсайз", "oversize"]),
    ],
  },

  rise: {
    label: "Посадка",
    kind: "select",
    keys: ["rise"],
    values: [v("low", "Низька", ["низьк", "low"]), v("mid", "Середня", ["середн", "mid", "medium"]), v("high", "Висока", ["висок", "high"])],
  },

  // silhouette — values are tagged with the product classes they make sense for
  cut: {
    label: "Силует",
    kind: "select",
    keys: ["cut"],
    values: [
      v("straight", "Прямі", ["прям"], { classes: ["bottom", "dress"] }),
      v("tapered", "Завужені", ["завужен", "tapered"], { classes: ["bottom"] }),
      v("wide", "Широкі", ["широк", "палаццо", "wide"], { classes: ["bottom"] }),
      v("flare", "Кльош", ["кльош", "flare"], { classes: ["bottom", "skirt"] }),
      v("skinny", "Скіні", ["скін", "skinny"], { classes: ["bottom"] }),
      v("mom", "Мом / бойфренд", ["мом", "mom", "бойфренд", "boyfriend", "банан"], { classes: ["bottom"] }),
      v("cargo", "Карго", ["карго", "cargo"], { classes: ["bottom"] }),
      v("a_line", "А-силует", ["а-силует", "a-line", "трапец"], { classes: ["dress", "skirt"] }),
      v("fitted", "Приталений", ["притален", "fitted"], { classes: ["dress"] }),
      v("sheath", "Футляр", ["футляр", "sheath"], { classes: ["dress"] }),
      v("wrap", "На запах", ["запах", "wrap"], { classes: ["dress", "skirt"] }),
      v("loose", "Вільний / оверсайз", ["вільн", "оверсайз", "балахон"], { classes: ["dress"] }),
      v("pencil", "Олівець", ["олівц", "pencil"], { classes: ["skirt"] }),
      v("pleated", "Плісе", ["плісе", "плісован", "pleated"], { classes: ["skirt"] }),
      v("sun", "Сонце / клеш", ["сонце", "клеш"], { classes: ["skirt"] }),
    ],
  },

  length: {
    label: "Довжина",
    kind: "select",
    keys: ["length"],
    values: [
      v("mini", "Міні", ["міні", "mini"], { classes: ["dress", "skirt"] }),
      v("midi", "Міді", ["міді", "midi"], { classes: ["dress", "skirt"] }),
      v("maxi", "Максі", ["максі", "maxi"], { classes: ["dress", "skirt"] }),
      v("short", "Коротка", ["коротк", "укорочен", "cropped"], { classes: ["outerwear"] }),
      v("long", "Довга", ["довг", "подовжен", "long"], { classes: ["outerwear"] }),
    ],
  },

  sleeve: {
    label: "Рукав",
    kind: "select",
    keys: ["sleeve"],
    values: [
      v("none", "Без рукавів", ["без рукав", "sleeveless"]),
      v("short", "Короткий", ["коротк", "short"]),
      v("three_quarter", "3/4", ["3/4", "три чверті", "three"]),
      v("long", "Довгий", ["довг", "long"]),
    ],
  },

  pattern: {
    label: "Візерунок",
    kind: "select",
    keys: ["pattern"],
    values: [
      v("solid", "Однотонний", ["однотон", "solid"]),
      v("stripe", "Смужка", ["смуж", "stripe"]),
      v("check", "Клітинка", ["клітин", "check", "plaid"]),
      v("floral", "Квітковий", ["квітк", "floral"]),
      v("dots", "Горошок", ["горош", "dots"]),
      v("animal", "Тваринний принт", ["тварин", "анімал", "леопард", "зебр", "animal"]),
      v("camouflage", "Камуфляж", ["камуфл", "хакі принт"]),
      v("print", "Принт / напис", ["принт", "напис", "логотип", "print"]),
    ],
  },

  occasion: {
    label: "Привід",
    kind: "multi",
    // the dress form's old `fit` field ("Повсякденне / Вечірнє / Коктейльне") was this concept
    keys: ["occasion", "fit"],
    values: [
      v("casual", "Повсякденне", ["повсякден", "casual"]),
      v("evening", "Вечірнє", ["вечірн", "evening"]),
      v("cocktail", "Коктейльне", ["коктейл", "cocktail"]),
      v("wedding", "Весільне", ["весіл", "наречен", "wedding"]),
      v("graduation", "Випускне", ["випускн", "graduation"]),
      v("office", "Для офісу", ["офіс", "діло", "office"]),
      v("party", "Для вечірки", ["вечірк", "party"]),
      v("beach", "Пляжне", ["пляж", "beach"]),
    ],
  },

  style: {
    label: "Стиль",
    kind: "multi",
    keys: ["style"],
    // the SAME ids Shopping Profile stores
    values: [
      v("casual", "Casual", ["casual", "кежуал"]),
      v("streetwear", "Streetwear", ["streetwear", "street", "стріт"]),
      v("classic", "Класика", ["класик", "класичн", "classic"]),
      v("minimal", "Мінімалізм", ["мінімал", "minimal"]),
      v("old_money", "Old money", ["old money", "олд мані"]),
      v("sport", "Спорт", ["спорт", "sport"]),
      v("business", "Ділова", ["діло", "business"]),
      v("romantic", "Романтична", ["романт", "romantic"]),
      v("vintage", "Вінтаж", ["вінтаж", "vintage", "ретро"]),
      v("boho", "Бохо", ["бохо", "boho"]),
    ],
  },

  // ---- shoes / bags specifics
  heel: {
    label: "Підбор",
    kind: "select",
    keys: ["heel"],
    values: [
      v("flat", "Плоска підошва", ["плоск", "flat", "без підбор"]),
      v("low", "Низький підбор", ["низьк", "low"]),
      v("medium", "Середній підбор", ["середн", "medium"]),
      v("high", "Високий підбор", ["висок", "high", "шпильк"]),
      v("platform", "Платформа / танкетка", ["платформ", "танкетк"]),
    ],
  },

  bagSize: {
    label: "Розмір сумки",
    kind: "select",
    keys: ["bagSize"],
    values: [v("mini", "Міні", ["міні", "mini"]), v("small", "Невелика", ["невелик", "small"]), v("medium", "Середня", ["середн", "medium"]), v("large", "Велика", ["велик", "large"])],
  },

  // ---- sizing context
  sizeSystem: {
    label: "Система розмірів",
    kind: "select",
    keys: ["sizeSystem"],
    values: [
      v("intl", "Міжнародна (XS–XL)", ["міжнарод", "intl", "international"]),
      v("eu", "EU", ["eu", "європ"]),
      v("ua", "UA", ["ua", "укр"]),
      v("us", "US", ["us", "сша"]),
      v("uk", "UK", ["uk", "британ"]),
    ],
  },

  shoeSizeSystem: {
    label: "Система розмірів взуття",
    kind: "select",
    keys: ["shoeSizeSystem"],
    values: [v("eu", "EU", ["eu", "європ"]), v("us", "US", ["us", "сша"]), v("uk", "UK", ["uk", "британ"]), v("cm", "Довжина стопи, см", ["см", "cm"])],
  },

  // ---- kids
  ageGroup: {
    label: "Вік",
    kind: "select",
    keys: ["ageGroup"],
    values: [
      v("m0_6", "0-6 міс.", ["0-6"]),
      v("m6_12", "6-12 міс.", ["6-12"]),
      v("y1_3", "1-3 роки", ["1-3"]),
      v("y3_6", "3-6 років", ["3-6"]),
      v("y6_10", "6-10 років", ["6-10"]),
      v("y10p", "10+ років", ["10+"]),
    ],
  },
};

export const FACET_IDS = Object.keys(FACETS);

// canonical write key -> facet id (the web advanced filters speak `attr.<key>`)
export const FACET_ID_BY_KEY = Object.fromEntries(Object.entries(FACETS).map(([id, facet]) => [facet.keys[0], id]));

// Facets whose ids are COLOUR families / brands / sizes are judged by dedicated rules (families above,
// brandSearch, size tokens) — they are not in FACETS but are canonical all the same:
export const CORE_FACET_IDS = ["audience", "department", "category", "brand", "color", "size", "condition", "price"];

/* ------------------------------ value recognition ----------------------------- */

const splitTokens = (raw) => {
  const list = Array.isArray(raw) ? raw : [raw];
  const out = [];
  for (const item of list) {
    if (item === null || item === undefined) continue;
    // "Літо, Осінь" / "Куртка; пальто" / "a|b" — a multi-value free text
    for (const part of String(item).split(/[,;|]/)) {
      const t = normalizeFacetText(part);
      if (t) out.push(t);
    }
  }
  return out;
};

const hasWordStartingWith = (text, stem) => {
  const s = normalizeFacetText(stem);
  if (!s) return false;
  let at = text.indexOf(s);
  while (at !== -1) {
    // a stem has to start a word (so "літ" ≠ "політ", "пух" ≠ "оксамит" is not a concern, "син" ≠ "насин")
    const before = at === 0 ? " " : text[at - 1];
    if (!/[a-zа-яіїєґ0-9]/.test(before)) return true;
    at = text.indexOf(s, at + 1);
  }
  return false;
};

/** Canonical value ids a RAW stored value (string | string[] | free text) stands for. [] = unknown. */
export const facetIdsOfValue = (facetId, raw) => {
  const facet = FACETS[facetId];
  if (!facet) return [];
  const found = [];

  for (const token of splitTokens(raw)) {
    for (const value of facet.values) {
      // the exact label always wins (that is what the form writes)
      if (normalizeFacetText(value.label) === token) {
        if (!found.includes(value.id)) found.push(value.id);
        continue;
      }
      const excluded = (value.excludes || []).some((x) => token.includes(normalizeFacetText(x)));
      if (excluded) continue;
      if (value.stems.some((stem) => hasWordStartingWith(token, stem))) {
        if (!found.includes(value.id)) found.push(value.id);
      }
    }
  }
  return found;
};

/** Canonical ids a LISTING states for a facet, reading every key the concept ever lived under. */
export const facetIdsOfAttributes = (facetId, attributes) => {
  const facet = FACETS[facetId];
  const attrs = attributes && typeof attributes === "object" ? attributes : {};
  if (!facet) return [];

  const found = [];
  for (const key of facet.keys) {
    if (attrs[key] === undefined || attrs[key] === null || attrs[key] === "") continue;
    // the dress form's legacy `fit` only ever meant "occasion"; never read it as anything else
    for (const id of facetIdsOfValue(facetId, attrs[key])) if (!found.includes(id)) found.push(id);
  }
  return found;
};

export const facetValueLabel = (facetId, valueId) => {
  const value = FACETS[facetId]?.values.find((x) => x.id === valueId);
  return value ? value.label : valueId;
};

// "Універсальне" fits any season; "Демісезон" fits spring and autumn. selected = ids the user wants.
export const seasonCompatible = (selected, have) => {
  if (!selected.length) return true;
  if (!have.length) return false;
  if (have.includes("all_year")) return true;
  return selected.some(
    (s) =>
      have.includes(s) ||
      (s === "demi" && (have.includes("spring") || have.includes("autumn"))) ||
      ((s === "spring" || s === "autumn") && have.includes("demi"))
  );
};

/** One facet's match rule: does a listing with `have` ids satisfy the user's `selected` ids? */
export const facetMatches = (facetId, selected, have) => {
  if (!selected || !selected.length) return true;
  if (facetId === "season") return seasonCompatible(selected, have);
  return selected.some((id) => have.includes(id));
};

/* ----------------------------- the conditional matrix ----------------------------- */

// Product classes. A class decides which facets exist for a product — everywhere.
export const CLASS_OF_SHOPPING_CATEGORY = {
  "t-shirts-tops": "top",
  "shirts-blouses": "top",
  "hoodies-sweatshirts": "knit",
  "jeans-trousers": "bottom",
  dresses: "dress",
  skirts: "skirt",
  suits: "suit",
  outerwear: "outerwear",
  sportswear: "sport",
  homewear: "home",
  underwear: "underwear",
  swimwear: "swim",
  maternity: "maternity",
  "clothing-other": "clothing",
  sneakers: "sneakers",
  boots: "boots",
  "loafers-flats": "shoes",
  sandals: "shoes",
  "home-shoes": "homeShoes",
  "shoes-other": "shoes",
  handbags: "bag",
  backpacks: "bag",
  clutches: "bag",
  "waist-bags": "bag",
  "bags-other": "bag",
  crossbody: "bag",
  "totes-shoppers": "bag",
  "travel-bags": "bag",
  "wallets-small-leather": "wallet",
  headwear: "hat",
  eyewear: "eyewear",
  belts: "belt",
  gloves: "gloves",
  "scarves-wraps": "scarf",
  "ties-bowties": "tie",
  watches: "watch",
  jewelry: "jewelry",
  "accessories-other": "accessory",
};

// The facets each class carries, in the order a form shows them. `size`/`shoeSize` etc. are the
// existing stored size fields; the others are FACETS above.
export const CLASS_FACETS = {
  top: ["size", "sizeSystem", "color", "colorSecondary", "material", "fitType", "sleeve", "pattern", "style", "season", "condition", "brand"],
  knit: ["size", "sizeSystem", "color", "colorSecondary", "material", "fitType", "pattern", "style", "season", "condition", "brand"],
  bottom: ["size", "sizeSystem", "color", "colorSecondary", "material", "fitType", "rise", "cut", "pattern", "style", "season", "condition", "brand"],
  dress: ["size", "sizeSystem", "color", "colorSecondary", "material", "cut", "length", "sleeve", "pattern", "occasion", "style", "season", "condition", "brand"],
  skirt: ["size", "sizeSystem", "color", "colorSecondary", "material", "cut", "length", "pattern", "style", "season", "condition", "brand"],
  suit: ["sizeTop", "sizeBottom", "color", "material", "fitType", "occasion", "style", "condition", "brand"],
  outerwear: ["size", "sizeSystem", "color", "colorSecondary", "material", "fitType", "length", "style", "season", "condition", "brand"],
  sport: ["size", "sizeSystem", "color", "material", "fitType", "condition", "brand"],
  home: ["size", "color", "material", "pattern", "season", "condition", "brand"],
  underwear: ["size", "color", "material", "condition", "brand"],
  swim: ["size", "color", "pattern", "condition", "brand"],
  maternity: ["size", "color", "material", "condition", "brand"],
  clothing: ["size", "color", "material", "condition", "brand"],
  sneakers: ["shoeSize", "shoeSizeSystem", "color", "colorSecondary", "material", "style", "condition", "brand"],
  boots: ["shoeSize", "shoeSizeSystem", "color", "colorSecondary", "material", "heel", "style", "season", "condition", "brand"],
  shoes: ["shoeSize", "shoeSizeSystem", "color", "colorSecondary", "material", "heel", "style", "season", "condition", "brand"],
  homeShoes: ["shoeSize", "color", "material", "condition", "brand"],
  bag: ["color", "colorSecondary", "material", "bagSize", "style", "condition", "brand"],
  wallet: ["color", "material", "condition", "brand"],
  hat: ["headSize", "color", "material", "season", "condition", "brand"],
  eyewear: ["color", "condition", "brand"],
  belt: ["color", "material", "condition", "brand"],
  gloves: ["size", "color", "material", "season", "condition", "brand"],
  scarf: ["color", "material", "pattern", "season", "condition", "brand"],
  tie: ["color", "material", "pattern", "condition", "brand"],
  watch: ["color", "material", "condition", "brand"],
  jewelry: ["color", "material", "condition", "brand"],
  accessory: ["color", "material", "condition", "brand"],
};

// Kids: age / height replace adult sizing; the adult-only cut facets disappear.
export const KIDS_FACET_BLACKLIST = ["sizeSystem", "colorSecondary", "fitType", "rise", "cut", "heel", "style", "occasion", "bagSize"];
export const KIDS_EXTRA_FACETS = ["ageGroup"];

/** Facet ids that apply to a product class (kids-aware). Unknown class -> the basics. */
export const facetsForClass = (cls, opts = {}) => {
  const kids = !!(opts && opts.kids);
  const base = CLASS_FACETS[cls] || ["size", "color", "material", "condition", "brand"];
  const list = kids ? base.filter((f) => !KIDS_FACET_BLACKLIST.includes(f)) : base;
  return kids ? [...KIDS_EXTRA_FACETS, ...list.filter((f) => !KIDS_EXTRA_FACETS.includes(f))] : list;
};

export const classOfShoppingCategory = (slug) => CLASS_OF_SHOPPING_CATEGORY[slug] || null;

// The Create Listing form's own `type` strings -> product class (department-independent).
export const CLASS_OF_FASHION_TYPE = {
  Футболки: "top",
  Майки: "top",
  "Майки/Топи": "top",
  "Блузки/Сорочки": "top",
  Сорочки: "top",
  "Кофти/Світшоти": "knit",
  "Штани/Джинси": "bottom",
  Плаття: "dress",
  Спідниці: "skirt",
  Костюми: "suit",
  "Верхній одяг": "outerwear",
  "Спортивний одяг": "sport",
  "Домашній одяг/Піжами": "home",
  "Спідня білизна": "underwear",
  Купальники: "swim",
  "Одяг для вагітних": "maternity",
  Взуття: "shoes",
  "Шапки/Головні убори": "hat",
  "Сумки/Рюкзаки": "bag",
  Аксесуари: "accessory",
  Інше: "clothing",
};

// The more specific shoe / accessory sub-type refines the class (sneakers have no heel; boots do).
export const CLASS_OF_SHOE_TYPE = {
  Кросівки: "sneakers",
  Черевики: "boots",
  Чоботи: "boots",
  Туфлі: "shoes",
  "Босоніжки/Сандалі": "shoes",
  "Домашнє взуття": "homeShoes",
  Інше: "shoes",
};

export const CLASS_OF_ACCESSORY_TYPE = {
  Окуляри: "eyewear",
  Ремінь: "belt",
  "Рукавиці/Рукавички": "gloves",
  Шарф: "scarf",
  "Краватка/Метелик": "tie",
  Годинник: "watch",
  Прикраси: "jewelry",
  Інше: "accessory",
};

// The separate "Аксесуари" catalogue category (gender -> section -> subcategory) -> product class.
export const CLASS_OF_ACCESSORY_SECTION = {
  "Голова / Обличчя": "accessory",
  "Головні убори": "hat",
  Вуха: "jewelry",
  "Шия — прикраси": "jewelry",
  "Шарфи і хустки": "scarf",
  "Краватки та метелики": "tie",
  "Руки — прикраси": "jewelry",
  "Ремені та пояси": "belt",
  "Щиколотки / Ноги": "jewelry",
  "Сумки та рюкзаки": "bag",
  "Гаманці та портмоне": "wallet",
  Парасолі: "accessory",
  Інше: "accessory",
};

// A subcategory can refine the section's class (sunglasses are eyewear, watches are watches, gloves are gloves).
export const CLASS_OF_ACCESSORY_SUBCATEGORY = {
  "Сонцезахисні окуляри": "eyewear",
  "Оправи / Оптичні окуляри": "eyewear",
  Годинники: "watch",
  Рукавички: "gloves",
  "Ремінці для годинника": "belt",
};

// What a legacy stored key is called when it is NOT the canonical write key (kept readable, never rewritten).
export const LEGACY_FACET_KEYS = Array.from(
  new Set(
    ["season", "material", "fitType", "rise", "cut", "length", "sleeve", "pattern", "occasion", "style", "heel", "bagSize", "sizeSystem", "shoeSizeSystem", "ageGroup"]
      .map((id) => FACETS[id].keys.slice(1))
      .reduce((a, b) => a.concat(b), [])
  )
);

/* ------------------------------ Create Listing fields ----------------------------- */

const COLOR_OPTIONS = COLOR_FAMILY_IDS.map((id) => id.charAt(0).toUpperCase() + id.slice(1));

const SIZE_FIELDS = {
  size: { name: "size", label: "Розмір", type: "text", placeholder: "Напр.: XS, S, M, L, XL або 44" },
  sizeTop: { name: "sizeTop", label: "Розмір верху", type: "text", placeholder: "Напр.: 48 або M" },
  sizeBottom: { name: "sizeBottom", label: "Розмір низу", type: "text", placeholder: "Напр.: 48 або W32 L32" },
  shoeSize: { name: "shoeSize", label: "Розмір взуття", type: "text", placeholder: "Напр.: 38, 42" },
  headSize: { name: "headSize", label: "Розмір головного убору", type: "text", placeholder: "Напр.: 56 см або універсальний" },
};

const SIZE_PLACEHOLDER_BY_CLASS = {
  bottom: "Напр.: S, 28/30, W32 L32 або 44",
  top: "Напр.: S, M, L або комір 39-41",
  underwear: "Напр.: 75B, M, L",
  gloves: "Напр.: S, M, L",
};

// A form field definition for a canonical facet; `attrs` lets conditional facets stay hidden until they matter.
export const fieldForFacet = (facetId, cls = "", attrs = {}) => {
  const a = attrs || {};

  if (SIZE_FIELDS[facetId]) {
    const f = { ...SIZE_FIELDS[facetId] };
    if (facetId === "size" && SIZE_PLACEHOLDER_BY_CLASS[cls]) f.placeholder = SIZE_PLACEHOLDER_BY_CLASS[cls];
    return f;
  }
  if (facetId === "color") return { name: "color", label: "Колір", type: "select", options: COLOR_OPTIONS };
  if (facetId === "colorSecondary") {
    // only once a main colour is chosen; a second colour is optional
    return a.color ? { name: "colorSecondary", label: "Другий колір (опціонально)", type: "select", options: COLOR_OPTIONS.filter((c) => c !== a.color) } : null;
  }
  if (facetId === "condition") return { name: "condition", label: "Стан", type: "select", options: ["Новий", "Б/В"] };
  if (facetId === "brand") return { name: "brand", label: "Бренд (опціонально)", type: "text", placeholder: "Напр.: Zara, Nike, Mango" };

  if (facetId === "sizeSystem") {
    return a.size || a.sizeTop || a.sizeBottom
      ? { name: "sizeSystem", label: "Система розмірів", type: "select", options: FACETS.sizeSystem.values.map((x) => x.label) }
      : null;
  }
  if (facetId === "shoeSizeSystem") {
    return a.shoeSize ? { name: "shoeSizeSystem", label: "Система розмірів взуття", type: "select", options: FACETS.shoeSizeSystem.values.map((x) => x.label) } : null;
  }

  const facet = FACETS[facetId];
  if (!facet) return null;

  let values = facet.values;
  // cut / length values are class-specific
  if (facetId === "cut" || facetId === "length") values = values.filter((x) => !x.classes || x.classes.includes(cls));
  if (!values.length) return null;

  const optional = facetId === "material" || facetId === "pattern" || facetId === "style" || facetId === "occasion" || facetId === "fitType" || facetId === "season";
  const label = optional && facetId !== "season" ? `${facet.label} (опціонально)` : facet.label;

  return {
    name: facet.keys[0],
    label,
    type: facet.kind === "multi" ? "multiselect" : "select",
    options: values.map((x) => x.label),
  };
};

/** The ordered, conditional Create Listing fields for a product class (kids-aware). */
export const fieldsForClass = (cls, attrs = {}, opts = {}) => {
  const out = [];
  for (const facetId of facetsForClass(cls, opts)) {
    const field = fieldForFacet(facetId, cls, attrs);
    if (field && !out.some((x) => x.name === field.name)) out.push(field);
  }
  return out;
};

/* --------------------- the facets a SEARCH/FILTER UI offers ---------------------- */

// Filterable canonical attributes (value-id facets only; colour/size/brand/condition/price have dedicated controls)
export const FILTERABLE_FACETS = ["season", "material", "fitType", "rise", "cut", "length", "sleeve", "pattern", "occasion", "style", "heel", "bagSize", "ageGroup"];

/** The attribute facets that make sense for a product class (what the filter sheet may show). */
export const attributeFacetsForClass = (cls, opts = {}) => facetsForClass(cls, opts).filter((f) => FILTERABLE_FACETS.includes(f));
